/**
 * Clinical case detail page.
 *
 * Renders individual before/after case studies with treatment details.
 * Imagery is strictly gated on patient consent via mayPublishCaseImages().
 * All current cases have consent: "unknown", so no imagery renders.
 */

import { useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Locale } from "@/content/types";
import { mayPublishCaseImages } from "@/content/types";
import { CASE_SLUGS, getCase } from "@/content/cases";
import { getService } from "@/content/services";
import { getDoctor } from "@/content/doctors";
import { clinicNode } from "@/lib/schema";
import { localizedPath } from "@/lib/locale";
import { motion } from "framer-motion";
import { AlertCircle, Lock } from "lucide-react";
import BookingCTA from "@/components/BookingCTA";

interface CasePageProps {
  locale: Locale;
}

export default function CasePage({ locale }: CasePageProps) {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useLanguage();

  if (!slug || !CASE_SLUGS.includes(slug)) {
    return (
      <>
        <Seo
          title={locale === "ar" ? "غير موجود" : "Not Found"}
          description={locale === "ar" ? "الصفحة غير موجودة" : "Page not found"}
          path={`/${locale}/cases/${slug}`}
          locale={locale}
          noindex
        />
        <main className="container mx-auto px-4 py-16">
          <h1 className="text-3xl font-bold text-white">
            {locale === "ar" ? "غير موجود" : "Not Found"}
          </h1>
        </main>
      </>
    );
  }

  const c = getCase(slug)!;
  const service = getService(c.service);
  const doctor = c.doctor ? getDoctor(c.doctor) : undefined;
  const canShowImages = mayPublishCaseImages(c);

  const title =
    locale === "ar"
      ? `${c.title[locale]} | عيادة بريميرو`
      : `${c.title[locale]} | Primero Dental Clinic`;

  const description =
    locale === "ar"
      ? `حالة سريرية: ${c.concern[locale]}`
      : `Case: ${c.concern[locale]}`;

  const consentNoticeText =
    locale === "ar"
      ? "الصور الفوتوغرافية السريرية تُنشر فقط بموافقة موثقة من المريض. يحترم فريقنا سرية المريض وخصوصيته بشكل كامل."
      : "Clinical photographs are published only with documented patient consent. We fully respect patient privacy and confidentiality.";

  return (
    <>
      <Seo
        title={title}
        description={description}
        path={`/${locale}/cases/${slug}`}
        locale={locale}
        schema={clinicNode(locale)}
      />
      <main className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {c.title[locale]}
          </h1>
          {service && (
            <p className="text-lg text-primary font-semibold">
              {service.name[locale]}
            </p>
          )}
        </motion.div>

        {/* Consent Notice (if images are not available) */}
        {!canShowImages && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mb-12 p-4 rounded-lg border border-slate-600/50 bg-slate-900/30 flex gap-3 items-start"
          >
            <Lock className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-slate-300 mb-1">
                {locale === "ar" ? "الخصوصية والموافقة" : "Privacy & Consent"}
              </h2>
              <p className="text-sm text-slate-400">{consentNoticeText}</p>
            </div>
          </motion.div>
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Before/After Section (or Placeholder) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="lg:col-span-1 space-y-4"
          >
            {canShowImages && c.images.before && (
              <div className="rounded-lg overflow-hidden border border-slate-700">
                <img
                  src={c.images.before.src}
                  alt={c.images.before.alt[locale]}
                  width={c.images.before.width}
                  height={c.images.before.height}
                  className="w-full h-auto"
                />
                <div className="p-3 bg-slate-900 text-center text-sm text-slate-400">
                  {locale === "ar" ? "قبل العلاج" : "Before"}
                </div>
              </div>
            )}
            {canShowImages && c.images.after && (
              <div className="rounded-lg overflow-hidden border border-slate-700">
                <img
                  src={c.images.after.src}
                  alt={c.images.after.alt[locale]}
                  width={c.images.after.width}
                  height={c.images.after.height}
                  className="w-full h-auto"
                />
                <div className="p-3 bg-slate-900 text-center text-sm text-slate-400">
                  {locale === "ar" ? "بعد العلاج" : "After"}
                </div>
              </div>
            )}
            {!canShowImages && (
              <div className="aspect-square rounded-lg border border-slate-700 bg-slate-900/50 flex items-center justify-center">
                <div className="text-center">
                  <Lock className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">
                    {locale === "ar"
                      ? "صور محمية بالموافقة"
                      : "Images protected by consent"}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    {locale === "ar"
                      ? "الصور متاحة فقط بموافقة المريض"
                      : "Photos available with patient consent"}
                  </p>
                </div>
              </div>
            )}
          </motion.div>

          {/* Case Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 space-y-8"
          >
            {/* Concern */}
            <div>
              <h3 className="text-lg font-semibold text-slate-200 mb-3">
                {locale === "ar" ? "المشكلة" : "Concern"}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {c.concern[locale]}
              </p>
            </div>

            {/* Starting Condition */}
            <div>
              <h3 className="text-lg font-semibold text-slate-200 mb-3">
                {locale === "ar" ? "الحالة قبل العلاج" : "Starting Condition"}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {c.startingCondition[locale]}
              </p>
            </div>

            {/* Diagnosis */}
            <div>
              <h3 className="text-lg font-semibold text-slate-200 mb-3">
                {locale === "ar" ? "التشخيص" : "Diagnosis"}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {c.diagnosis[locale]}
              </p>
            </div>

            {/* Treatment Plan */}
            <div>
              <h3 className="text-lg font-semibold text-slate-200 mb-3">
                {locale === "ar" ? "خطة العلاج" : "Treatment Plan"}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {c.treatmentPlan[locale]}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Treatment Steps */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-white mb-6">
            {locale === "ar" ? "مراحل العلاج" : "Treatment Steps"}
          </h2>
          <div className="space-y-4">
            {c.steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: locale === "ar" ? 20 : -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-lg border border-slate-700 bg-slate-900/50"
              >
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary/50 flex items-center justify-center text-primary font-semibold">
                      {i + 1}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-2">
                      {step.title[locale]}
                    </h3>
                    <p className="text-slate-400">{step.body[locale]}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Result */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-12 p-6 rounded-lg border border-primary/30 bg-primary/5"
        >
          <h2 className="text-2xl font-bold text-white mb-4">
            {locale === "ar" ? "النتيجة" : "Result"}
          </h2>
          <p className="text-slate-300 leading-relaxed text-lg">
            {c.result[locale]}
          </p>
        </motion.section>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="grid md:grid-cols-2 gap-6 mb-12"
        >
          {/* Doctor */}
          {doctor && (
            <a
              href={localizedPath(locale, `/doctors/${doctor.slug}`)}
              className="p-6 rounded-lg border border-slate-700 bg-slate-900/50 hover:border-primary/50 hover:bg-slate-900/80 transition-all"
            >
              <h3 className="text-sm font-semibold text-slate-300 mb-2">
                {locale === "ar" ? "الطبيب" : "Doctor"}
              </h3>
              <p className="text-lg font-semibold text-white">
                {doctor.name[locale]}
              </p>
              <p className="text-sm text-primary">{doctor.specialty[locale]}</p>
            </a>
          )}

          {/* Service */}
          {service && (
            <a
              href={localizedPath(locale, `/services/${service.slug}`)}
              className="p-6 rounded-lg border border-slate-700 bg-slate-900/50 hover:border-primary/50 hover:bg-slate-900/80 transition-all"
            >
              <h3 className="text-sm font-semibold text-slate-300 mb-2">
                {locale === "ar" ? "الخدمة" : "Service"}
              </h3>
              <p className="text-lg font-semibold text-white">
                {service.name[locale]}
              </p>
              <p className="text-sm text-slate-400 mt-2 line-clamp-2">
                {service.summary[locale]}
              </p>
            </a>
          )}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-center py-8"
        >
          <p className="text-slate-300 mb-4">
            {locale === "ar"
              ? "هل أنت مهتم بهذه الخدمة؟"
              : "Interested in this treatment?"}
          </p>
          <BookingCTA
            trigger={
              <button className="px-8 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-colors">
                {locale === "ar"
                  ? "احجز موعد استشارة"
                  : "Book Consultation"}
              </button>
            }
          />
        </motion.div>
      </main>
    </>
  );
}
